import React, { useState, useMemo, useEffect } from "react";
import {
  Skeleton,
  Input,
  Select,
  message,
  Form,
  Button,
  Spin,
  Modal as AntdModal,
  Table,
} from "antd";
import { FaTrashAlt } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Timesheetapi } from "../../middleware/interceptor";
import { LoadingOutlined } from "@ant-design/icons";

const parseStatus = (active) => {
  if (active === null || active === undefined) return "Active";
  if (active === 4) return "Inactive";
  return "Active";
};

export default function Users() {
  const [countsLoading, setCountsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [tempdata, setTempData] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState("");
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [roleUpdatingId, setRoleUpdatingId] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [allEmployeeOptions, setAllEmployeeOptions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isUpdateEnabled, setIsUpdateEnabled] = useState(false);
  const [currentAdminRow, setCurrentAdminRow] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [updatingEmployeeId, setUpdatingEmployeeId] = useState(null);
  const [azureLoading, setAzureLoading] = useState(false);
  const [baseCounts, setBaseCounts] = useState({ total: 0, active: 0, inactive: 0 });
  const [pageSize, setPageSize] = useState(10);


  const EXCLUDED_ROLES = ["HR", "Accountant"];

  const successMsg = (msg) =>
    messageApi.open({ type: "success", content: msg, duration: 1.5 });

  const fetchRoles = async () => {
    try {
      const response = await Timesheetapi.get("api/User/getAllRoles");
      const roles = response.data?.Roles || [];
      const mapped = roles
        .filter((r) => !EXCLUDED_ROLES.includes(r.rolename))
        .map((r) => ({
          value: r.rolename,
          label: r.rolename,
          role_id: r.role_id,
        }));
      setRoleOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };



  const fetchEmployees = async () => {
    setCountsLoading(true);
    setTableLoading(true);
    try {
      const response = await Timesheetapi.get("api/User/getAllEmployees");
      const employees = (response.data?.Employees || []).map((emp) => ({
        User_id: emp.employee_id,
        name: emp.employee_name || "-",
        email: emp.employee_email || "-",
        phone: emp.Emp_Phone || "-",
        role: emp.employee_role || " ",
        status: "Checking...", // ← default while Azure loads
        team_members: [],
        _raw: emp,
      }));

      const sorted = [...employees].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );

      const allOptions = sorted.map((e) => ({
        value: e.User_id,
        label: e.name,
        role: e.role,
      }));
      setAllEmployeeOptions(allOptions);
      setEmployeeOptions(allOptions.filter((e) => e.role !== "Admin"));

      const merged = await fetchTeamMembers(sorted);
      setTempData(merged);
      setRawData(merged);

      // ✅ Auto check Azure status after employees load
      fetchAzureStatuses(merged);

    } catch (err) {
      console.error("Failed to fetch employees:", err);
      toast.error("Failed to load users. Please try again.");
    } finally {
      setTableLoading(false);
      setCountsLoading(false);
    }
  };


  const fetchAzureStatuses = async (employeeList) => {
    setAzureLoading(true);
    try {
      const emails = employeeList
        .filter((emp) => emp.email && emp.email !== "-")
        .map((emp) => emp.email);

      const res = await Timesheetapi.post("api/User/getAzureStatusBatch", { emails });

      if (!res.data?.success) return;

      const statusMap = {};
      (res.data.data || []).forEach((item) => {
        statusMap[item.email] = item.status;
      });

      // ✅ Compute updated list ONCE
      const updated = employeeList.map((emp) => ({
        ...emp,
        status: statusMap[emp.email] ?? "Unknown",
      }));

      // ✅ Set both using the same `updated` array
      setTempData(updated);

      setBaseCounts({
        total: updated.length,
        active: updated.filter((u) => u.status === "Active").length,
        inactive: updated.filter((u) => u.status === "Inactive").length,
      });

    } catch (err) {
      console.error("Azure batch status check failed:", err);
    } finally {
      setAzureLoading(false);
    }
  };


  const fetchTeamMembers = async (employeeList) => {
    try {
      const response = await Timesheetapi.get("api/User/getAllTeamMembers");
      if (!response.data?.success) return employeeList;

      const allRows = response.data.data || [];

      // DEBUG — remove after fix confirmed
      console.log("All reporting rows:", JSON.stringify(allRows));

      const merged = employeeList.map((emp) => {
        const roleLower = (emp.role || "").toLowerCase();
        let preSelected = [];

        if (roleLower === "admin") {
          const empId = parseInt(emp.User_id);
          const rows = allRows.filter(
            (r) => Number(r.admin_id) === empId
          );
          rows.forEach((row) => {
            preSelected = [
              ...preSelected,
              ...(row.manager_ids || []).map(Number),
              ...(row.teamlead_ids || []).map(Number),
              ...(row.member_ids || []).map(Number),
            ];
          });
        }
        else if (roleLower === "project manager") {
          const empId = parseInt(emp.User_id);
          const rows = allRows.filter((r) =>
            (r.manager_ids || []).map(Number).includes(empId)
          );
          rows.forEach((row) => {
            preSelected = [
              ...preSelected,
              ...(row.teamlead_ids || []).map(Number),
              ...(row.member_ids || []).map(Number),
            ];
          });
        }
        else if (roleLower === "team lead") {
          const empId = parseInt(emp.User_id);
          const rows = allRows.filter((r) =>
            (r.teamlead_ids || []).map(Number).includes(empId)
          );
          rows.forEach((row) => {
            preSelected = [
              ...preSelected,
              ...(row.member_ids || []).map(Number),
            ];
          });
        }

        preSelected = [...new Set(preSelected)].filter(
          (id) => id !== parseInt(emp.User_id)
        );

        // DEBUG
        if (roleLower === "team lead") {
          console.log(`TL ${emp.User_id} preSelected:`, preSelected);
        }

        return { ...emp, team_members: preSelected };
      });

      return merged;
    } catch (err) {
      console.error("Failed to fetch team members:", err);
      return employeeList;
    }
  };

  const handleTeamMembersChange = (userId, newMembers, allOptions) => {
    const managerIds = newMembers.filter(id => {
      const emp = allOptions.find(e => e.value === id);
      return emp?.role?.toLowerCase() === "project manager";
    });
    const teamleadIds = newMembers.filter(id => {
      const emp = allOptions.find(e => e.value === id);
      return emp?.role?.toLowerCase() === "team lead";
    });
    const memberIds = newMembers.filter(id => {
      const emp = allOptions.find(e => e.value === id);
      return emp?.role?.toLowerCase() === "team member";
    });
    setUpdatingEmployeeId(userId);
    setSelectedMembers(newMembers);
    setIsUpdateEnabled(newMembers.length >= 0);
    setIsUpdateEnabled(true);
    if (newMembers.length === 0) {
      setIsUpdateEnabled(false);
    }


    setCurrentAdminRow({ managerIds, teamleadIds, memberIds });
    setTempData((prev) =>
      prev.map((u) =>
        u.User_id === userId ? { ...u, team_members: newMembers } : u
      )
    );
  };

  const handleSaveTeamMembers = async () => {
    setSaveLoading(true);
    try {
      const employeeId = updatingEmployeeId;
      const emp = allEmployeeOptions.find(e => e.value === employeeId);
      const roleLower = (emp?.role || "").toLowerCase();

      const managerIds = currentAdminRow?.managerIds || currentAdminRow?.manager_ids || [];
      const teamleadIds = currentAdminRow?.teamleadIds || currentAdminRow?.teamlead_ids || [];
      const memberIds = currentAdminRow?.memberIds || currentAdminRow?.member_ids || [];

      let payload = {};

      if (roleLower === "admin") {
        payload = {
          admin_id: employeeId,
          manager_ids: JSON.stringify(managerIds),
          teamlead_ids: JSON.stringify(teamleadIds),
          member_ids: JSON.stringify(memberIds),
        };
      }
      else if (roleLower === "project manager") {
        payload = {
          admin_id: null,
          manager_ids: JSON.stringify([employeeId]),
          teamlead_ids: JSON.stringify(teamleadIds),
          member_ids: JSON.stringify(memberIds),
        };
      }
      else if (roleLower === "team lead") {
        payload = {
          admin_id: null,
          manager_ids: null,
          teamlead_ids: JSON.stringify([employeeId]),
          member_ids: JSON.stringify(memberIds),
        };
      }

      const response = await Timesheetapi.post("api/User/updateTeamMembers", payload);

      if (response.data?.success) {
        toast.success("Team members saved successfully!");
        setIsUpdateEnabled(false);
        setUpdatingEmployeeId(null);
        await fetchEmployees();
      } else {
        toast.error(response.data?.message || "Save failed.");
      }
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Failed to save team members.");
    } finally {
      setSaveLoading(false);
    }
  };




  useEffect(() => {
    fetchRoles();
    fetchEmployees();
  }, []);

  const applyFilters = () => {
    let filtered = [...tempdata];
    if (selectedRole !== "All") {
      filtered = filtered.filter((u) => u.role === selectedRole);
    }
    if (selectedStatus !== "All") {
      filtered = filtered.filter((u) => u.status === selectedStatus);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      filtered = filtered.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(q) ||
          (u.role || "").toLowerCase().includes(q) ||
          (u.status || "").toLowerCase().includes(q)
      );
    }
    setRawData(filtered);
  };

  useEffect(() => { applyFilters(); }, [searchText, selectedRole, selectedStatus, tempdata]);
  useEffect(() => { setData(rawData); }, [rawData]);

  // useEffect(() => {
  //   setTotalCount(rawData.length);
  //   setActiveCount(rawData.filter((u) => u.status === "Active").length);
  //   setInactiveCount(rawData.filter((u) => u.status === "Inactive").length);
  // }, [rawData]);


  const handleRoleChange = async (userId, newRoleName) => {
    const selectedRoleObj = roleOptions.find((r) => r.value === newRoleName);
    if (!selectedRoleObj) return;

    // Get old role from current tempdata
    const currentEmp = tempdata.find((u) => u.User_id === userId);
    const oldRoleName = currentEmp?.role || "";

    setRoleUpdatingId(userId);
    try {
      // ── STEP 1: existing role update (unchanged) ──
      const response = await Timesheetapi.post("api/User/updateEmployeeRole", {
        employee_id: userId,
        role_id: selectedRoleObj.role_id,
      });

      if (response.data?.success) {
        setTempData((prev) =>
          prev.map((u) =>
            u.User_id === userId ? { ...u, role: newRoleName } : u
          )
        );
        toast.success(`Role updated to "${newRoleName}" successfully`);

        // ── STEP 2: call new migrate function ──
        try {
          await Timesheetapi.post("api/User/migrateEmployeeRole", {
            employee_id: userId,
            old_role: oldRoleName,
            new_role: newRoleName,
          });
        } catch (migrateErr) {
          // Migration failure won't block role update success
          console.error("Role migration failed:", migrateErr);
        }
        await fetchEmployees();

      } else {
        toast.error(response.data?.message || "Failed to update role");
      }
    } catch (err) {
      console.error("Role update failed:", err);
      toast.error("Failed to update role. Please try again.");
    } finally {
      setRoleUpdatingId(null);
    }
  };




  const openCreateModal = () => {
    setIsModalOpen(true);
    setEdit(false);
    setEditId(null);
    form.resetFields();
  };

  const openDeleteModal = (record) => {
    setDeleteId(record.User_id);
    setDeleteUserName(record.name);
    setDeleteModal(true);
  };

  const handleSubmit = async (values) => {
    setModalLoading(true);
    try {
      const selectedRoleObj = roleOptions.find((r) => r.value === values.role);

      const response = await Timesheetapi.post("api/User/createNewUser", {
        name: values.name,
        email: values.email,
        phone: values.contact,
        status: values.status,
        role_id: selectedRoleObj.role_id,
      });

      if (response.data?.success) {
        await fetchEmployees();
        toast.success("User added successfully.");
        setIsModalOpen(false);
        form.resetFields();
      }

      else {
        if (response.data?.message?.toLowerCase().includes("email already exists")) {
          form.setFields([
            {
              name: "email",
              errors: ["Email already exists. Please use a different email."],
            },
          ]);
        } else {
          toast.error(response.data?.message || "Failed to create user");
        }
      }
    } catch (err) {
      console.error("Create failed:", err);
      toast.error("Failed to create user. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };



  const confirmDelete = async () => {
    setModalLoading(true);
    try {
      const response = await Timesheetapi.post("api/User/deleteEmployee", {
        employee_id: deleteId,
      });

      if (response.data?.success) {
        setTempData((prev) => prev.filter((u) => u.User_id !== deleteId));
        toast.success("User deleted successfully");
        setDeleteModal(false);
        setDeleteId(null);
        setDeleteUserName("");
      } else {
        toast.error(response.data?.message || "Failed to delete user");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete user. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  const STATUS_OPTIONS = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const columns = useMemo(
    () => [
      {
        title: "#",
        key: "index",
        width: 55,
        align: "center",
        render: (_, __, index) => (
          <span className="text-[12px] text-gray-400">{index + 1}</span>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 220,
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (val) => (
          <span className="text-[13px] font-[500] text-gray-800">{val || "-"}</span>
        ),
      },

      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 220,
        render: (val) => (
          <span className="text-[13px] text-gray-700">{val || "-"}</span>
        ),
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        width: 150,
        render: (val) => (
          <span className="text-[13px] text-gray-700">{val || "-"}</span>
        ),
      },

      {
        title: "Role",
        dataIndex: "role",
        key: "role",
        width: 200,
        render: (val, record) => (
          <Select
            value={val}
            size="small"
            style={{ width: 160, height: "30px" }}
            options={roleOptions}
            disabled={record.status !== "Active"}
            onChange={(newVal) => handleRoleChange(record.User_id, newVal)}
          />
        ),
      },
      {
        title: "Team Members",
        dataIndex: "team_members",
        key: "team_members",
        width: 220,
        render: (val, record) => {
          const roleLower = (record.role || "").toLowerCase();

          // ✅ Already defined — now actually USE it below
          const activeEmployeeOptions = allEmployeeOptions.filter(e => {
            const emp = tempdata.find(t => t.User_id === e.value);
            return emp?.status === "Active";
          });

          let filteredOptions = [];

          if (roleLower === "admin") {
            filteredOptions = activeEmployeeOptions.filter(  // ✅ was allEmployeeOptions
              (e) => e.value !== record.User_id &&
                e.role?.toLowerCase() !== "admin"
            );
          }
          else if (roleLower === "project manager") {
            const myTeam = val || [];
            filteredOptions = activeEmployeeOptions.filter(  // ✅ was allEmployeeOptions
              (e) => myTeam.includes(e.value) ||
                e.role?.toLowerCase() === "team lead" ||
                e.role?.toLowerCase() === "team member"
            );
            filteredOptions = filteredOptions.filter(
              (e) => e.value !== record.User_id &&
                e.role?.toLowerCase() !== "admin" &&
                e.role?.toLowerCase() !== "project manager"
            );
          }
          else if (roleLower === "team lead") {
            const myTeam = val || [];
            filteredOptions = activeEmployeeOptions.filter(  // ✅ was allEmployeeOptions
              (e) => myTeam.includes(e.value) ||
                e.role?.toLowerCase() === "team member"
            );
            filteredOptions = filteredOptions.filter(
              (e) => e.value !== record.User_id
            );
          }
          else {
            filteredOptions = [];
          }

          const isDisabled =
            roleLower === "team member" ||
            record.status === "Inactive";

          const activeVal = (val || []).filter(id => {
            const emp = tempdata.find(t => t.User_id === id);
            return emp?.status === "Active";
          });

          return (
            <Select
              mode="multiple"
              size="small"
              value={activeVal}
              disabled={isDisabled}
              placeholder={isDisabled ? "N/A" : "Select members"}
              style={{ width: "100%", minWidth: 180, height: "32px" }}
              options={filteredOptions}
              onChange={(newVal) =>
                handleTeamMembersChange(record.User_id, newVal, allEmployeeOptions)
              }
              showSearch
              optionFilterProp="label"
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) =>
                omittedValues.length > 0 ? (
                  <span>{omittedValues.length} Employees</span>
                ) : null
              }
            />
          );
        },
      },

      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 160,
        render: (val, record) => (
          <Select
            value={val}
            size="small"
            style={{ width: 100, height: "30px" }}
            options={STATUS_OPTIONS}
            disabled={true}
          />
        ),
      },



      {
        title: "Action",
        key: "action",
        width: 80,
        align: "center",
        fixed: "right",
        render: (_, record) => (
          <div className="flex justify-center gap-3">
            <FaTrashAlt
              onClick={() => openDeleteModal(record)}
              className="text-[14px] text-[#ff4d4d] cursor-pointer"
            />
          </div>
        ),
      },
    ],
    [data, roleOptions, roleUpdatingId, employeeOptions, allEmployeeOptions, tempdata]
  );

  return (
    <div>
      <ToastContainer />
      {contextHolder}


      <Spin
        spinning={roleUpdatingId !== null || saveLoading || azureLoading}
        tip={
          azureLoading
            ? "Checking user status..."
            : saveLoading
              ? "Saving..."
              : "Updating role..."
        }
        size="large"
      >

        <div className="mx-auto px-0">
          <div className="mb-[5px]">
            <h2 className="page-heading relative bottom-[-10px]">USERS</h2>
          </div>
          <div className="mt-6">
            <div className="flex flex-wrap gap-4">

              <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="text-center">
                  <span className="text-[12px] font-[500] text-gray-600">Total Users</span>
                  <div className="text-[28px] font-[600] text-blue-600 mt-2">
                    {countsLoading
                      ? <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" />
                      : baseCounts.total}
                  </div>
                  <span className="text-[11px] text-gray-500 block mt-1">All Roles</span>
                </div>
              </div>

              <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="text-center">
                  <span className="text-[12px] font-[500] text-gray-600">Active Users</span>
                  <div className="text-[28px] font-[600] text-green-600 mt-2">
                    {countsLoading
                      ? <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" />
                      : baseCounts.active}
                  </div>
                  <span className="text-[11px] text-gray-500 block mt-1">Status: Active</span>
                </div>
              </div>

              <div className="flex-1 min-w-[200px] p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="text-center">
                  <span className="text-[12px] font-[500] text-gray-600">Inactive Users</span>
                  <div className="text-[28px] font-[600] text-red-600 mt-2">
                    {countsLoading
                      ? <Skeleton active paragraph={false} title={false} className="w-[60px] mx-auto" />
                      : baseCounts.inactive}
                  </div>
                  <span className="text-[11px] text-gray-500 block mt-1">Status: Inactive</span>
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-between items-end gap-[15px] mt-3 z-[9]">
            <div className="flex items-end gap-2 flex-wrap">
              <button
                onClick={openCreateModal}
                className="bg-blue-500 text-white py-[5px] px-3 rounded flex items-center justify-center w-[145px] h-[32px]"
              >
                <span className="mr-2">
                  <i className="fa fa-plus-circle text-[16px] relative top-[1px]"></i>
                </span>
                <span className="text-[13px]">Create a User</span>
              </button>
              <div className="w-[175px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Role:</label>
                <Select
                  value={selectedRole}
                  onChange={(val) => setSelectedRole(val)}
                  options={[{ value: "All", label: "All" }, ...roleOptions]}
                  placeholder="Select Role"
                />
              </div>
              <div className="w-[150px] flex flex-col gap-[5px]">
                <label className="text-[13px] pl-2">Status:</label>
                <Select
                  value={selectedStatus}
                  onChange={(val) => setSelectedStatus(val)}
                  options={[{ value: "All", label: "All" }, ...STATUS_OPTIONS]}
                  placeholder="Select Status"
                />
              </div>
              <div className="flex flex-col gap-[5px]">
                <label className="text-[13px]">Search Users</label>
                <Input
                  placeholder="Search by Name, Role, or Status..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  prefix={<i className="fa fa-search text-gray-400"></i>}
                  className="h-[32px] w-[320px]"
                />
              </div>

              <div className="flex flex-col gap-[5px]">
                <label className="text-[13px] opacity-0">Update</label>
                <Button
                  type="primary"
                  disabled={!isUpdateEnabled}
                  onClick={handleSaveTeamMembers}
                  className="h-[32px] w-[100px]"
                >
                  Update
                </Button>
              </div>

            </div>
          </div>
          <AntdModal
            className="workitem-popup"
            open={isModalOpen}
            footer={null}
            onCancel={() => setIsModalOpen(false)}
            closable
            maskClosable={false}
            keyboard={false}
            width={650}
          >
            <div className="bg-white rounded-lg">
              <h3
                className="text-[14px] font-[600] mb-4 px-[22px] mx-[-22px]"
                style={{ borderBottom: "1px solid #ddd", paddingBottom: "15px" }}
              >
                {edit ? "Update User" : "Create a User"}
              </h3>

              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <div className="task-project-modal">
                  <div className="grid grid-cols-2 gap-x-4">

                    <Form.Item
                      label="Name"
                      name="name"
                      rules={[{ required: true, message: "Please enter the user name" }]}
                    >
                      <Input className="h-10" placeholder="Enter Full Name" />
                    </Form.Item>

                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[{ required: true, message: "Please enter the email" }]}
                    >
                      <Input className="h-10" placeholder="Enter Email" />
                    </Form.Item>

                    <Form.Item
                      label="Contact"
                      name="contact"
                      rules={[{ required: true, message: "Please enter the phone number" }]}
                    >
                      <Input className="h-10" placeholder="Enter Phone Number" />
                    </Form.Item>

                    <Form.Item
                      label="Role"
                      name="role"
                      initialValue={roleOptions[0]?.value}
                      rules={[{ required: true, message: "Please select a role" }]}
                    >
                      <Select
                        className="h-10"
                        placeholder="Select Role"
                        options={roleOptions}              // ← dynamic from API
                      />
                    </Form.Item>

                    <Form.Item
                      label="Status"
                      name="status"
                      initialValue="Active"
                      rules={[{ required: true, message: "Please select a status" }]}
                    >
                      <Select className="h-10" placeholder="Select Status" options={STATUS_OPTIONS} />
                    </Form.Item>

                  </div>

                  <div className="text-right mt-6">
                    <Button
                      className="w-[100px]"
                      type="default"
                      onClick={() => setIsModalOpen(false)}
                      style={{ marginRight: "8px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="w-[100px]"
                      type="primary"
                      htmlType="submit"
                      loading={modalLoading}
                    >
                      {edit
                        ? modalLoading ? "Updating..." : "Update"
                        : modalLoading ? "Creating..." : "Create"}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </AntdModal>

          <AntdModal
            title={
              <>
                <ExclamationCircleFilled style={{ color: "orange", marginRight: "8px" }} />
                Confirm
              </>
            }
            open={deleteModal}
            onCancel={() => { setDeleteModal(false); setDeleteId(null); }}
            closable
            maskClosable={false}
            keyboard={false}
            footer={[
              <Button
                key="cancel"
                onClick={() => { setDeleteModal(false); setDeleteId(null); }}
              >
                Cancel
              </Button>,
              <Button
                key="confirm"
                type="primary"
                danger
                loading={modalLoading}
                onClick={confirmDelete}
              >
                Delete
              </Button>,
            ]}
          >
            <p>Are you sure you want to delete user <strong>"{deleteUserName}"</strong>?</p>
            <p>This action cannot be undone.</p>
          </AntdModal>

          {/* ── Table ── */}
          <div className="mt-5 overflow-auto custom-table absolute w-[98%] custom-user-table">
            <Spin spinning={tableLoading} tip="Loading...">
              <Table
                columns={columns}
                dataSource={data}
                rowKey="User_id"
                size="small"
                pagination={{
                  pageSize: pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total) => `Total ${total} users`,
                  onShowSizeChange: (current, size) => setPageSize(size),
                  onChange: (page, size) => setPageSize(size),
                }}
                bordered
                scroll={{ x: "max-content", y: 480 }}
                sticky={{ offsetHeader: 0 }}
                locale={{ emptyText: "No users found" }}
                rowClassName={(_, index) =>
                  index % 2 === 0 ? "bg-white" : "bg-[#fafafa]"
                }
              />
            </Spin>
          </div>

        </div>
      </Spin>
    </div>
  );
}